import React, { useContext, useState } from "react";
import { Button } from "@mui/material";
import { getDocs } from "firebase/firestore";
import { format, eachDayOfInterval } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import ReportDateRangeDialog from "./shared/ReportDateRangeDialog";

const isComplete = (t) =>
  !!(t.markedCompletedTime || t.markedCompleted || t.completed);

/**
 * MOD Completion Compliance — for each day in the range, the % of MOD daily
 * tasks completed. Reads properties/{id}/modTasks/dailyReports/{YYYY-MM-DD}.
 */
export default function GenerateMODCompliance({ user }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async ({ start, end }) => {
    setIsGenerating(true);
    try {
      const days = eachDayOfInterval({ start, end });

      const rows = await Promise.all(
        days.map(async (day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const ref = propertyCollection(
            selectedClient,
            selectedProperty,
            "modTasks",
            "dailyReports",
            dateStr
          );
          const snap = await getDocs(ref);
          const total = snap.size;
          const completed = snap.docs.filter((d) => isComplete(d.data())).length;
          return { dateStr, total, completed };
        })
      );

      const withData = rows.filter((r) => r.total > 0);
      if (withData.length === 0) {
        alert("No MOD tasks found in that date range.");
        return;
      }

      const totals = withData.reduce(
        (acc, r) => ({ total: acc.total + r.total, completed: acc.completed + r.completed }),
        { total: 0, completed: 0 }
      );

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "MOD Completion Compliance",
        dateRange: { start, end },
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const pct = (c, t) => (t > 0 ? `${Math.round((c / t) * 100)}%` : "—");
      const body = withData.map((r) => [
        r.dateStr,
        String(r.total),
        String(r.completed),
        pct(r.completed, r.total),
      ]);
      body.push([
        { content: "OVERALL", styles: { fontStyle: "bold" } },
        { content: String(totals.total), styles: { fontStyle: "bold", halign: "right" } },
        { content: String(totals.completed), styles: { fontStyle: "bold", halign: "right" } },
        { content: pct(totals.completed, totals.total), styles: { fontStyle: "bold", halign: "right" } },
      ]);

      autoTable(pdf, {
        startY: contentTop,
        head: [["Date", "Tasks", "Completed", "Compliance"]],
        body,
        styles: { font: "helvetica", fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: {
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
        },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("MOD_Compliance", { ext: "pdf", start, end })
      );
      setOpen(false);
    } catch (e) {
      console.error("Error generating MOD Compliance:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="contained" fullWidth onClick={() => setOpen(true)}>
        MOD Completion Compliance
      </Button>
      <ReportDateRangeDialog
        open={open}
        title="MOD Completion Compliance"
        description="Daily % of MOD tasks completed across the selected date range."
        busy={isGenerating}
        onClose={() => setOpen(false)}
        onSubmit={handleGenerate}
      />
    </>
  );
}
